import { useEffect, useMemo, useState } from 'react';
import { Route as RouteIcon } from 'lucide-react';
import { formatDateTime } from '@nu/shared';
import { api } from '../../api/client.js';
import Modal from '../../components/Modal.jsx';
import { problem } from '../bookings/util.js';
import { clockAfter, hm, km, nowHm } from './nearby.js';
import PlanMap from './PlanMap.jsx';

// Office: Auto-assign. Choose a team, or one engineer on their own (teams
// split up to survey, TKT-000003), and how many surveys; the server picks
// the nearest jobs still to survey and nobody's (from the ticked jobs, or
// all those on the board), routes them from the team's (or their) van and times them:
// 10 minutes a survey plus the drive, and when they'd finish setting off now.
// Nothing is given out until Assign: then everyone in the team gets the jobs
// in their app (and a message); one engineer: only them.

export default function AutoAssign({ open, jobIds, fromTicked, nearby, onClose, onAssign }) {
  const [who, setWho] = useState(''); // 't:<team id>' or 'p:<person id>'
  const [count, setCount] = useState('10');
  const [plan, setPlan] = useState(null);
  const [start, setStart] = useState(''); // the time it was worked out
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setPlan(null); setError(null); } }, [open]);
  useEffect(() => { setPlan(null); }, [who, count]);

  const people = useMemo(() => new Map((nearby?.people || []).map((p) => [p.id, p])), [nearby]);
  const teams = useMemo(() => nearby?.teams || [], [nearby]);
  // Everyone in a team today, on their own, by name.
  const singles = useMemo(() => [...new Set(teams.flatMap((t) => t.memberIds))].map((id) => people.get(id)).filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name, 'en-GB')), [teams, people]);
  const [kind, whoId] = [who.slice(0, 1), who.slice(2)];

  const workOut = async () => {
    setBusy(true);
    setError(null);
    try {
      setPlan(await api('/surveys/auto-plan', { method: 'POST', body: { ...(kind === 'p' ? { personIds: [whoId] } : { teamId: Number(whoId) }), count: Number(count), jobIds } }));
      setStart(nowHm());
    } catch (e) {
      setError(problem(e));
    } finally {
      setBusy(false);
    }
  };

  const team = kind === 't' ? teams.find((t) => String(t.id) === whoId) : kind === 'p' ? { memberIds: [whoId] } : null;
  return (
    <Modal
      open={open}
      wide
      title="Auto-assign surveys"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          {plan?.count > 0
            ? <button type="button" className="btn primary" onClick={() => onAssign(plan.stops.map((s) => s.jobId), plan.team.memberIds)}>Assign these {plan.count} to {plan.team.name}</button>
            : <button type="button" className="btn primary with-icon" disabled={!who || !(Number(count) > 0) || busy} onClick={workOut}><RouteIcon size={16} aria-hidden="true" className={busy ? 'spin' : ''} /> {busy ? 'Working out…' : 'Work it out'}</button>}
        </>
      )}
    >
      <div className="stack">
        <p className="small muted">
          Picks the nearest surveys that are still to do and nobody’s, from {fromTicked ? `the ${jobIds.length} jobs you ticked` : 'the jobs on the board (with its filters)'}, in the best driving order from the team’s van (or the engineer’s, on their own). Each survey counts as 10 minutes on site.
        </p>
        <label className="fld">
          <span>Team or engineer</span>
          <select value={who} onChange={(e) => setWho(e.target.value)}>
            <option value="">Choose a team or one engineer…</option>
            <optgroup label="Teams">
              {teams.map((t) => (
                <option key={t.id} value={`t:${t.id}`}>{t.name}: {t.memberIds.map((id) => people.get(id)?.name).filter(Boolean).join(', ')}</option>
              ))}
            </optgroup>
            {singles.length > 0 && (
              <optgroup label="One engineer on their own">
                {singles.map((p) => <option key={p.id} value={`p:${p.id}`}>{p.name}</option>)}
              </optgroup>
            )}
          </select>
          {team && (
            <span className="small muted">
              {team.memberIds.map((id) => {
                const p = people.get(id);
                return p ? `${p.name}: ${p.position ? `${p.position.van || 'van'}, seen ${formatDateTime(p.position.at)}` : 'no van location'}` : null;
              }).filter(Boolean).join(' · ')}
            </span>
          )}
        </label>
        <label className="fld auto-assign-count">
          <span>Surveys to do</span>
          <input type="number" min="1" max="60" inputMode="numeric" value={count} onChange={(e) => setCount(e.target.value)} />
        </label>
        {error && <p className="notice-line notice-bad">{error}</p>}
        {plan && (
          <>
            <div className="auto-assign-sum">
              <div><span className="small muted">Surveys</span><strong>{plan.count}</strong><span className="small muted">{hm(plan.surveySeconds)} on site</span></div>
              <div><span className="small muted">Driving</span><strong>{hm(plan.travelSeconds)}</strong><span className="small muted">{km(plan.travelMetres / 1000)}</span></div>
              <div><span className="small muted">Total</span><strong>{hm(plan.totalSeconds)}</strong><span className="small muted">from setting off</span></div>
              <div><span className="small muted">Finish about</span><strong>{clockAfter(start, plan.totalSeconds) || '–'}</strong><span className="small muted">setting off now ({start})</span></div>
            </div>
            <p className="small">
              {plan.count < plan.asked && `Only ${plan.count} free survey${plan.count === 1 ? '' : 's'} to give out. `}
              Starting from {plan.from.name}’s van{plan.from.van ? ` (${plan.from.van})` : ''}{plan.from.place ? `, ${plan.from.place}` : ''}. Driving back at the end isn’t counted.
              {' '}Nothing is sent to their app until you press Assign.
            </p>
            <PlanMap plan={plan} start={start} />
            <ol className="auto-assign-stops small">
              {plan.stops.map((s) => (
                <li key={s.jobId}>
                  <strong>{s.address}</strong>, {s.postcode}
                  <span className="muted"> · {hm(s.driveSeconds)} drive · done about {clockAfter(start, s.doneAfterSeconds) || hm(s.doneAfterSeconds)}</span>
                </li>
              ))}
            </ol>
          </>
        )}
      </div>
    </Modal>
  );
}
